import { useRef, useState, useEffect, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, setRhythmBpm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 13 — Sixteenth Arpeggio Accents.
 *
 * Jason's chart: vi–IV–I–V in G major (Em, C, G, D), voiced B-E-G, C-E-G,
 * B-D-G, A-D-F♯, one bar of 16 sixteenths per chord. The arpeggio restarts
 * on its low note at the start of every group, so the GROUPING itself is the
 * accent: 3+3+3+3+4 (B-E-G ×4, then B-E-G-E), and 5+5+3+3. 4+4+4+4 is the
 * regular baseline — "you may not even want to call them accents".
 */
const PATTERNS: Record<string, { groups: number[]; label: string; description: string }> = {
  "4+4+4+4":   { groups: [4, 4, 4, 4],    label: "4+4+4+4",   description: "Regular — on the beat, so not really an accent" },
  "3+3+3+3+4": { groups: [3, 3, 3, 3, 4], label: "3+3+3+3+4", description: "Jason's main grouping — four threes and a four" },
  "5+5+3+3":   { groups: [5, 5, 3, 3],    label: "5+5+3+3",   description: "Two fives, then the six split as two threes" },
};

/** Jason's voicings for vi, IV, I, V — semitones above the tonic (in G: B-E-G, C-E-G, B-D-G, A-D-F♯). */
const CHORDS: { name: string; roman: string; voicing: [number, number, number]; rootSemis: number }[] = [
  { name: "vi", roman: "vi", voicing: [4, 9, 12],  rootSemis: 9 },
  { name: "IV", roman: "IV", voicing: [5, 9, 12],  rootSemis: 5 },
  { name: "I",  roman: "I",  voicing: [4, 7, 12],  rootSemis: 0 },
  { name: "V",  roman: "V",  voicing: [2, 7, 11],  rootSemis: 7 },
];

/** Inside a group the arpeggio climbs L-M-H, then turns: L-M-H-M (Jason's 4), L-M-H-M-H. */
const GROUP_SHAPE = [0, 1, 2, 1, 2, 1];

const GROUP_COLORS = [
  "bg-amber-500/70",
  "bg-cyan-500/70",
  "bg-rose-500/70",
  "bg-emerald-500/70",
  "bg-violet-500/70",
];

export default function PianoIntermediate13() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "13");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="G" availableQualities={["major"]}>
        <PIShell
          title="Sixteenth Arpeggio Accents"
          exerciseGoal="Sixteen sixteenth notes per bar, grouped 3+3+3+3+4 and 5+5+3+3 over vi–IV–I–V. The arpeggio restarts on each group, and that is the accent."
          bpm={80}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [patternKey, setPatternKey] = useState<string>("3+3+3+3+4");
  const [bpm, setBpm] = useState(80);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(-1);
  const handleRef = useRef<RhythmHandle | null>(null);
  const pattern = PATTERNS[patternKey];

  const stop = () => {
    handleRef.current?.stop();
    handleRef.current = null;
    setRunning(false);
    setStep(-1);
  };

  useEffect(() => {
    if (running) setRhythmBpm(bpm, 0.05);
  }, [bpm, running]);

  const play = async () => {
    kickAudio();
    stop();
    setRunning(true);
    // One bar of sixteenths per chord. Each group starts again on the low note.
    const cellTone: number[] = [];
    const accentCells = new Set<number>();
    for (const g of pattern.groups) {
      accentCells.add(cellTone.length);
      for (let j = 0; j < g; j++) cellTone.push(GROUP_SHAPE[j]);
    }
    const events: { beat: number; notes: string[]; durationSteps: number; velocity: number }[] = [];
    CHORDS.forEach((ch, bar) => {
      for (let i = 0; i < 16; i++) {
        events.push({
          beat: bar * 16 + i,
          notes: [midiToPitchName(tonicMidi + ch.voicing[cellTone[i]], k.useFlats)],
          durationSteps: 1,
          velocity: accentCells.has(i) ? 0.9 : 0.45,
        });
      }
      // Left hand: the chord root, held for the bar.
      events.push({
        beat: bar * 16,
        notes: [midiToPitchName(tonicMidi - 12 + ch.rootSemis - (ch.rootSemis > 6 ? 12 : 0), k.useFlats)],
        durationSteps: 16,
        velocity: 0.6,
      });
    });
    handleRef.current = await playRhythm(events, {
      bpm,
      subdivide: 4,
      totalSteps: 64,
      loop: true,
      click: true,
      beatsPerBar: 4,
      onStep: (s) => setStep(s % 16),
    });
  };

  const chordNames = CHORDS.map((ch) => {
    const n = k.nameForPc((k.tonicPc + ch.rootSemis) % 12);
    return ch.roman === "vi" ? `${n}m` : n;
  });
  const voicingNames = CHORDS.map((ch) => ch.voicing.map((o) => k.nameForPc((k.tonicPc + o) % 12)).join("-"));

  const highlights: KeyHighlight[] = [
    { degree: 1, tone: "root",  label: "I" },
    { degree: 4, tone: "voice", label: "IV" },
    { degree: 5, tone: "voice", label: "V" },
    { degree: 6, tone: "voice", label: "vi" },
  ];

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram highlights={highlights} octaves={2} startOctave={3} playable />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          The four chord roots of vi–IV–I–V in the current key:{" "}
          <strong>{chordNames.join(" – ")}</strong>. In the video Jason plays it in G major.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The concept" title="Sixteen notes, grouped irregularly">
          <p>
            Sixteen sixteenth notes make up a bar of four: 1 e &amp; a, 2 e &amp; a, 3 e &amp; a,
            4 e &amp; a. Group them 4+4+4+4 and every group lands on a beat — regular, so you may
            not even call it an accent. Group them <strong>3+3+3+3+4</strong> instead: the
            arpeggio starts again on its low note every three sixteenths, then once more for a
            final four. That restart is what you hear as the accent.
          </p>
          <p>
            Jason's chord shapes in {k.tonicName}: {CHORDS.map((ch, i) => `${chordNames[i]} = ${voicingNames[i]}`).join(" · ")}.
            Each group of three is low-middle-high; the four is low-middle-high-middle.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "An accent is just an irregular grouping or a unique phrase which is not with the
            counting of the listener."
          </PIQuote>
          <PIInsight>
            <p>
              It's still a bar of four. Keep the pulse — tap it, or play the accents in your left
              hand to reinforce them — while the right hand's groups cut across it.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The groupings" title="See · hear · feel the groupings">
          <AccentGroupGrid
            patternKey={patternKey}
            step={running ? step : -1}
            onPatternChange={(key) => { stop(); setPatternKey(key); }}
          />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={play}
              disabled={running}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300 disabled:opacity-50"
            >
              ▶ Loop {pattern.label} over {chordNames.join(" – ")}
            </button>
            {running && (
              <button
                onClick={stop}
                className="rounded-lg border border-rose-400/60 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 hover:bg-rose-500/20"
              >
                ■ Stop
              </button>
            )}
            <label className="flex items-center gap-2 text-[12px] text-cyan-200">
              <span className="font-mono uppercase tracking-wider text-cyan-300/80">Tempo</span>
              <input
                type="range" min={50} max={120} step={1} value={bpm}
                onChange={(e) => setBpm(Number(e.target.value))}
                className="h-1 w-32 accent-amber-400"
              />
              <span className="w-10 font-mono text-[11px] text-cyan-100">{bpm}</span>
            </label>
          </div>
          <p className="mt-3 text-[14px] text-cyan-50/95">
            <strong>{pattern.label}</strong> · <em>{pattern.description}</em>
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Make it melodic" title="Put a melody on the accents">
          <p>
            Start the groups from the top instead and the accented notes become a melody line. Jason
            keeps the lower notes of the chord still and floats the top note on every accent — on a
            single G minor chord at first.
          </p>
          <p>
            Then he plays a song that uses exactly this phrasing: Bryan Adams'{" "}
            <em>Summer of '69</em>, in D. Two chords, D and A. On D, the D and A stay pivoted at the
            bottom while the accented top notes walk E, F♯, G, F♯, E, F♯; on A, the A and E stay
            put while the top walks B, C♯, D, C♯, B, C♯.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Practice ladder" title="Sixteen minutes">
          <PracticeLadder
            intro={
              <>
                One grouping per rung. Keep the chords the same — only the grouping changes. Count
                "1 e &amp; a" out loud until the bar of four never slips.
              </>
            }
            rungs={[
              {
                title: "Regular 4+4+4+4",
                minutes: 3,
                accent: "amber",
                detail: "Every group on a beat. Lock the sixteenths and the chord changes in before you push against the grid.",
              },
              {
                title: "3+3+3+3+4 from the low note",
                minutes: 5,
                accent: "cyan",
                detail: "Low-middle-high four times, then low-middle-high-middle. Play the accents in the left hand too.",
              },
              {
                title: "3+3+3+3+4 from the top",
                minutes: 4,
                accent: "rose",
                detail: "Start each group on the high note and float it — the accents become a melody (Summer of '69).",
              },
              {
                title: "5+5+3+3",
                minutes: 4,
                accent: "violet",
                detail: "Two fives and two threes. Slow it down and count each group before you play it.",
              },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke: AccentGroupGrid ─────────────────────────────────────── */
function AccentGroupGrid({
  patternKey,
  step,
  onPatternChange,
}: {
  patternKey: string;
  step: number;
  onPatternChange: (k: string) => void;
}) {
  const pattern = PATTERNS[patternKey];

  // Build cell metadata: cellIndex → { groupIndex, isAccent }
  const cellMeta: { groupIndex: number; isAccent: boolean }[] = [];
  let cursor = 0;
  pattern.groups.forEach((size, gi) => {
    for (let j = 0; j < size; j++) {
      cellMeta.push({ groupIndex: gi, isAccent: j === 0 });
    }
    cursor += size;
  });

  const beatLabels = ["1", "e", "&", "a", "2", "e", "&", "a", "3", "e", "&", "a", "4", "e", "&", "a"];

  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-4">
      {/* Pattern selector buttons */}
      <div className="mb-4 flex flex-wrap gap-2">
        {Object.entries(PATTERNS).map(([key, p]) => (
          <button
            key={key}
            onClick={() => onPatternChange(key)}
            className={`rounded-lg border px-3 py-1.5 text-[12px] font-bold transition-colors ${
              key === patternKey
                ? "border-amber-400/70 bg-amber-500/15 text-amber-200"
                : "border-cyan-400/20 text-cyan-300/70 hover:bg-cyan-500/10 hover:text-cyan-200"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* 16-cell grid */}
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: "repeat(16, minmax(0, 1fr))" }}
      >
        {cellMeta.map((meta, i) => {
          const isStep = i === step;
          const colorClass = GROUP_COLORS[meta.groupIndex % GROUP_COLORS.length];
          return (
            <motion.div
              key={i}
              animate={{
                scale: isStep ? 1.15 : 1,
                boxShadow: isStep ? "0 0 0 2px #FCD34D" : "0 0 0 0 transparent",
              }}
              transition={{ duration: 0.08 }}
              className={`relative flex aspect-square items-center justify-center rounded ${
                meta.isAccent
                  ? `${colorClass} ring-1 ring-white/20`
                  : "bg-zinc-800/50"
              } ${isStep ? "ring-2 ring-amber-300" : ""}`}
            >
              {meta.isAccent ? (
                <span className="text-[11px] font-bold text-zinc-900">▾</span>
              ) : (
                <span className="h-1 w-1 rounded-full bg-zinc-600" />
              )}
              {/* Group start: left border gap marker */}
            </motion.div>
          );
        })}
      </div>

      {/* Colored group-band row */}
      <div
        className="mt-1 grid gap-1"
        style={{ gridTemplateColumns: "repeat(16, minmax(0, 1fr))" }}
      >
        {cellMeta.map((meta, i) => {
          const colorBands = [
            "bg-amber-500/30",
            "bg-cyan-500/30",
            "bg-rose-500/30",
            "bg-emerald-500/30",
            "bg-violet-500/30",
          ];
          const isFirst = cellMeta[i - 1]?.groupIndex !== meta.groupIndex;
          const isLast = cellMeta[i + 1]?.groupIndex !== meta.groupIndex;
          return (
            <div
              key={i}
              className={`h-2 ${colorBands[meta.groupIndex % colorBands.length]} ${
                isFirst ? "rounded-l-full" : ""
              } ${isLast ? "rounded-r-full" : ""}`}
            />
          );
        })}
      </div>

      {/* Beat labels */}
      <div
        className="mt-1 grid gap-1"
        style={{ gridTemplateColumns: "repeat(16, minmax(0, 1fr))" }}
      >
        {beatLabels.map((lab, i) => (
          <span
            key={i}
            className={`text-center text-[9px] font-mono ${
              lab === "1" ? "font-bold text-amber-300" : "text-cyan-300/50"
            }`}
          >
            {lab}
          </span>
        ))}
      </div>

      <p className="mt-3 text-[12px] italic text-cyan-300/70">
        Amber triangles mark accented cells (group downbeats). The coloured band shows
        group boundaries. Beat labels: 1 e &amp; a · 2 e &amp; a · 3 e &amp; a · 4 e &amp; a.
      </p>
    </div>
  );
}

/* ── Shared helpers ───────────────────────────────────────────────── */
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        style={{ overflow: "hidden" }}
      >
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote
        className="text-[17px] leading-relaxed text-cyan-50/95"
        style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}
      >
        {children}
      </blockquote>
      {attribution && (
        <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">
          — {attribution}
        </figcaption>
      )}
    </figure>
  );
}
